import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useFieldArray, useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form, FormGroup, Label, Input, Table, InputGroup, InputGroupText } from 'reactstrap';
import { InvoicingDto } from '../../types';
import { Button } from '../../components';
import { invoiceService } from '../../services/InvoiceService';
import { generateYears } from '../../common/Utils';

interface GenerateAnnualInvoicesFormProps {
    onClose?: () => void;
}

const GenerateAnnualInvoicesForm = ({ onClose }: GenerateAnnualInvoicesFormProps) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const schema = yup
        .object({
            id: yup.number().required().default(0),
            institutionId: yup.number().required().default(0),
            institutionTitle: yup.string().notRequired().nullable(),
            status: yup.number().required().default(3),
            fiscalYear: yup.string().notRequired().nullable(),
            description: yup.string().notRequired().nullable(),
            notificationEmail: yup.string().notRequired().nullable(),
            amount: yup.number().notRequired().nullable(),
            tiers: yup
                .array()
                .of(
                    yup
                        .object({
                            numProfessions: yup.number().required().min(1),
                            perProfessionFee: yup.number().required().min(0),
                        })
                        .defined(),
                )
                .required(),
            invoiceDate: yup.string().required(),
            automaticallySendOn: yup.string().notRequired().nullable(),
            sendAutomatically: yup.boolean().notRequired().nullable(),
            createDate: yup.string().notRequired().nullable(),
            lastUpdatedDate: yup.string().nullable(),
            rowVersion: yup.string().notRequired().nullable(),
            invoiceNumber: yup.string().notRequired().nullable(),
            lateFee: yup.number().notRequired().nullable().min(0),
            convenienceFee: yup.number().notRequired().nullable().min(0),
            dueAmount: yup.number().notRequired().nullable(),
            shortUrl: yup.string().notRequired().nullable(),
            programIds: yup.string().notRequired().nullable(),
            programTitles: yup.string().notRequired().nullable(),
            isDeleted: yup.boolean().required().default(false),
            invoiceType: yup.number().required().default(1),
            recipientId: yup.number().nullable().notRequired(),
            stripeInvoiceUrl: yup.string().nullable().notRequired(),
            pdfUrl: yup.string().nullable().notRequired(),
            dueDate: yup.string().nullable().notRequired(),
        })
        .defined();

    const { handleSubmit, register, control, watch, reset, setValue, formState: { errors } } = useForm({
        resolver: yupResolver(schema as any),
        defaultValues: {
            ...({} as InvoicingDto),
            description: 'CAAHEP Accredited Program Fee',
            notificationEmail: 'receivables@caahep.org',
            invoiceDate: moment([new Date().getFullYear(), 5, 1]).format('YYYY-MM-DD'),
            lateFee: 0,
            convenienceFee: 0,
            tiers: [
                {
                    numProfessions: 1,
                    perProfessionFee: 660,
                },
                {
                    numProfessions: 2,
                    perProfessionFee: 550,
                },
                {
                    numProfessions: 3,
                    perProfessionFee: 385,
                },
                {
                    numProfessions: 4,
                    perProfessionFee: 275,
                },
                {
                    numProfessions: 5,
                    perProfessionFee: 220,
                },
            ],
        },
    });
    const { fields, append } = useFieldArray({
        name: 'tiers',
        control,
    });
    const formValues = watch();

    // Extract complex expressions to separate variables
    const fiscalYearExists = !!formValues?.fiscalYear && +formValues?.fiscalYear > 0;
    const invoiceDateExists = !!formValues?.invoiceDate;

    useEffect(() => {
        if (fiscalYearExists) {
            if (invoiceDateExists) {
                const existingInvoiceDate = moment(formValues.invoiceDate);

                if (existingInvoiceDate.year() !== +(formValues.fiscalYear || 0)) {
                    setValue(
                        'invoiceDate',
                        moment([
                            +(formValues.fiscalYear || 0),
                            existingInvoiceDate.month(),
                            existingInvoiceDate.date(),
                        ]).format('YYYY-MM-DD'),
                    );
                }
            } else {
                setValue('invoiceDate', moment([+(formValues.fiscalYear || 0), 5, 1]).format('YYYY-MM-DD'));
            }
        }
    }, [fiscalYearExists, invoiceDateExists, formValues, setValue]);

    const onSubmit = async (values: any) => {
        setIsGenerating(true);
        const toastId = toast.info('Generating invoices...', {
            closeOnClick: false,
            draggable: false,
            autoClose: false,
        });

        try {
            await invoiceService.generateAnnualInvoices(values);
            toast.update(toastId, {
                type: 'success',
                render: 'Invoices are being generated, and you will be notified by email when they are complete!',
                autoClose: 3000,
                closeOnClick: true,
                draggable: true,
            });
            onClose?.();
        } catch {
            toast.update(toastId, {
                type: 'error',
                render: 'Error generating invoices',
                autoClose: 5000,
                closeOnClick: true,
                draggable: true,
            });
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <Form noValidate onSubmit={handleSubmit(onSubmit)}>
            <FormGroup>
                <Label for="fiscalYear">Fiscal Year</Label>
                <Controller
                    name="fiscalYear"
                    control={control}
                    render={({ field }) => (
                        <Input
                            type="select"
                            {...field}
                            value={field.value || ''}
                            onChange={(e) => field.onChange(e.target.value || null)}
                        >
                            <option value="">Select...</option>
                            {generateYears().map((ct) => (
                                <option key={ct} value={ct}>
                                    {ct}
                                </option>
                            ))}
                        </Input>
                    )}
                />
            </FormGroup>
            <FormGroup>
                <Label for="invoiceDate">Invoice Date</Label>
                <Controller
                    name="invoiceDate"
                    control={control}
                    render={({ field }) => <Input type="date" {...field} />}
                />
                {errors?.invoiceDate?.message && (
                    <span className={'text-danger'}>{errors.invoiceDate.message}</span>
                )}
            </FormGroup>
            <FormGroup>
                <Label for="description">Description</Label>
                <Controller
                    name="description"
                    control={control}
                    render={({ field }) => <Input {...field} value={field.value || ''} />}
                />
            </FormGroup>
            <FormGroup>
                <Label for="notificationEmail">
                    Email address(es) to be notified when invoice generation is complete (comma separated):{' '}
                </Label>
                <Controller
                    name="notificationEmail"
                    control={control}
                    render={({ field }) => <Input {...field} value={field.value || ''} />}
                />
            </FormGroup>
            <FormGroup>
                <Label for="amount">Accredited Program Fee Structure</Label>
                <div className={`col-lg-6`}>
                    <Table>
                        <thead>
                            <tr>
                                <th>{`# Programs at Institution`}</th>
                                <th>{`Per Program Fee`}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {fields.map((f, i) => (
                                <tr key={f.id}>
                                    <td>{f.numProfessions}</td>
                                    <td>
                                        <InputGroup>
                                            <InputGroupText>$</InputGroupText>
                                            <Controller
                                                name={`tiers.${i}.perProfessionFee` as any}
                                                control={control}
                                                render={({ field }) => (
                                                    <Input type="number" {...field} value={field.value ?? ''}
                                                        onChange={(e) => field.onChange(e.target.value === '' ? undefined : +e.target.value)} />
                                                )}
                                            />
                                            <input type="hidden" {...register(`tiers.${i}.numProfessions` as any)} />
                                        </InputGroup>
                                    </td>
                                    <td>
                                        <Button
                                            color={`danger`}
                                            type={'button'}
                                            onClick={() => {
                                                const oldTiers = [...formValues.tiers];
                                                oldTiers.splice(i, 1);
                                                reset({
                                                    ...formValues,
                                                    tiers: oldTiers.map((t, i) => {
                                                        return {
                                                            numProfessions: i + 1,
                                                            perProfessionFee: t.perProfessionFee,
                                                        };
                                                    }),
                                                });
                                            }}
                                        >
                                            <i className={`mdi mdi-close`} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            <tr>
                                <td colSpan={3}>
                                    <Button
                                        type={'button'}
                                        color={`primary`}
                                        onClick={() =>
                                            append({
                                                numProfessions: fields.length + 1,
                                                perProfessionFee: 0,
                                            })
                                        }
                                    >
                                        <i className={`mdi mdi-plus`} />
                                        <span className={`ms-2`}>{`Add a Fee Tier`}</span>
                                    </Button>
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </div>
            </FormGroup>

            <div>
                <Button type="submit" disabled={isGenerating}>
                    {isGenerating ? 'Generating...' : 'Generate Annual Invoices'}
                </Button>
            </div>
        </Form>
    );
};

export default GenerateAnnualInvoicesForm;
